// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@west/App/Components/Common/XngZone";
import HeaderClickable, { HeaderClickableStyles } from "@west/App/Components/Header/HeaderClickable";
import xCommonUpdate from "@west/App/Handlers/XNG/Common";
import { CustomAppState } from "@west/App/Store/Reducers";
import { getIsCheckoutPages } from "@west/App/Store/Selectors/CheckoutSelectors";
import { getMainNavigationWidget } from "@west/App/Store/Selectors/WidgetSelectors";
import Search from "@west/XDL/Icons/Search";
import { xClassNames } from "@west/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        isCheckoutPages: getIsCheckoutPages(state, true),
        isTabletScreen: state.xCommon.isTabletScreen,
        showSearchSectionOnMobile: (state as CustomAppState).xCommon.showSearchSectionOnMobile,
    };
};

const mapDispatchToProps = {
    xCommonUpdate,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
    // XNG-Changes
    mainNavigationWrapper?: InjectableCss;
    hiddenZipCode?: InjectableCss;
    actionWrapper?: InjectableCss;
    headerClickableStyles?: HeaderClickableStyles;
    mobileSearchContainer?: InjectableCss;
    tabletSearchContainer?: InjectableCss;
    mobileCloseSearchIcon?: IconPresentationProps;
    mobileSearchWrapper?: InjectableCss;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            padding: 20px 45px;
            margin: 0 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                height: 78px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 42px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            padding: 20px 45px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        css`
                            flex-grow: 8;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    linkListHidden: {
        below: "lg",
    },
};

const LogoLinksSearch: FC<Props> = ({
    id,
    isTabletScreen,
    isCheckoutPages,
    showSearchSectionOnMobile,
    xCommonUpdate,
}) => {
    const { isInShell } = useShellContext();

    const mobileSearchHandler = (isOpen: boolean) => {
        xCommonUpdate({ key: "showSearchSectionOnMobile", value: isOpen });
    };

    const mainNavigationWidgetField = useAppSelector(state => getMainNavigationWidget(state));

    if (isCheckoutPages) {
        return (
            <StyledWrapper {...logoLinksSearchStyles.container} {...({ isCheckoutPages } as any)}>
                <StyledWrapper>
                    <XngZone contentId={id} zoneName="Logo" />
                </StyledWrapper>
                <StyledWrapper>
                    <XngZone contentId={id} zoneName="CheckoutNeedSupport" />
                </StyledWrapper>
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper {...logoLinksSearchStyles.container}>
            {showSearchSectionOnMobile && (
                <StyledWrapper
                    {...logoLinksSearchStyles.tabletSearchContainer}
                    className={xClassNames.append(showSearchSectionOnMobile && "opened")}
                >
                    <XngZone contentId={id} zoneName="Search" />
                    <Icon {...logoLinksSearchStyles.mobileCloseSearchIcon} onClick={() => mobileSearchHandler(false)} />
                </StyledWrapper>
            )}
            {!showSearchSectionOnMobile && (
                <>
                    <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
                        {isTabletScreen && (
                            <StyledWrapper {...logoLinksSearchStyles.mainNavigationWrapper}>
                                {createWidgetElement("Header/MainNavigation", {
                                    fields: { ...mainNavigationWidgetField },
                                })}
                            </StyledWrapper>
                        )}
                        <XngZone contentId={id} zoneName="Logo" />
                        <Hidden below="sm" {...logoLinksSearchStyles.hiddenZipCode}>
                            <XngZone contentId={id} zoneName="ZipCode" />
                        </Hidden>
                    </StyledWrapper>
                    <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper}>
                        <XngZone contentId={id} zoneName="Search" />
                    </StyledWrapper>
                    <StyledWrapper {...logoLinksSearchStyles.actionWrapper}>
                        <HeaderClickable
                            variant="menu"
                            onClick={() => mobileSearchHandler(true)}
                            extendedStyles={logoLinksSearchStyles.headerClickableStyles}
                            aria-label={translate("Open Search")}
                        />
                        <XngZone contentId={id} zoneName="LeftActions" normalWidth={true} />
                        <XngZone contentId={id} zoneName="Actions" normalWidth={true} />
                        {isInShell && <XngZone contentId={id} zoneName="CheckoutNeedSupport" />}
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
